<template>
  <div class="box-integration-modern">
    <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-content">
          <i class="fas fa-spinner fa-spin loading-icon"></i>
          <h3>Carregando boxes...</h3>
        </div>
      </div>

      <!-- Content when not loading -->
      <div v-if="!loading">
        <!-- Box Selection View -->
        <div v-if="!selectedBox" class="box-selection-container">
          <!-- No Results State -->
          <div v-if="boxs.length == 0" class="no-results">
            <i class="fas fa-inbox no-results-icon"></i>
            <h3 class="no-results-title">Nenhuma Box disponível</h3>
            <p class="no-results-text">
              Não foram encontradas integrações de Box configuradas
            </p>
          </div>

          <!-- Box Selection Grid -->
          <div v-if="boxs.length > 0" class="box-selection-section">
            <div class="section-header">
              <h2 class="section-title">
                <i class="fas fa-cube mr-3"></i>
                Selecione uma Box
              </h2>
              <p class="section-subtitle">
                Escolha a plataforma para integração dos medicamentos
              </p>
              <div class="row">
                <div class="col-md-6">
                  <label for="box-start-date">Data de início na Box</label>
                  <div class="input-group">
                    <input
                      id="box-start-date"
                      v-model="box_start_date"
                      type="date"
                      class="form-control"
                      aria-describedby="box-start-help"
                      @change="handleBoxStartDate"
                    />
                    <div class="input-group-append">
                      <v-btn
                        class="btn-modern btn-modern-save"
                        @click="saveConfigBox"
                        >SALVAR</v-btn
                      >
                    </div>
                  </div>
                  <small id="box-start-help" class="box-help">
                    Primeiro dia que a Box produz para este residente.
                  </small>
                </div>
                <!-- Data do corte vem depois: é consequência da data de início (início − 12 dias) -->
                <div class="col-md-6">
                  <label for="box-cut-off-date"
                    >Data do corte (calculada)</label
                  >
                  <input
                    id="box-cut-off-date"
                    v-model="box_cut_off_date"
                    type="date"
                    readonly
                    class="form-control"
                  />
                </div>
                <div class="col-md-12">
                  <ErrorsMessages :errors="errors" />
                </div>
              </div>
            </div>
            <div v-if="!resident_ok">
              <h6
                class="resident-alert-without-data-to-register-box text-center"
                v-if="!cpf"
              >
                Informe o CPF nos dados cadastrais do residente!
              </h6>
              <h6
                class="resident-alert-without-data-to-register-box text-center"
                v-if="!gender_name"
              >
                Informe o gênero nos dados cadastrais do residente!
              </h6>
            </div>
            <div class="boxes-grid">
              <div
                v-for="box in boxs"
                :key="box.id"
                class="box-card"
                :class="{ 'box-selected': selectedBoxId == box.id }"
                @click="selectBox(box)"
              >
                <input
                  type="radio"
                  :name="'box-radio'"
                  :id="'box-' + box.id"
                  :value="box.id"
                  v-model="selectedBoxId"
                  class="box-radio"
                />

                <div class="box-content">
                  <!-- Logo Background -->
                  <div
                    v-if="box.logo"
                    class="box-logo-bg"
                    :style="{
                      backgroundImage: `url(${box.logo})`,
                    }"
                  ></div>

                  <!-- Card Content -->
                  <div class="box-info" :class="{ 'with-logo': box.logo }">
                    <div class="box-header">
                      <div class="box-icon">
                        <i class="fas fa-cube"></i>
                      </div>
                      <h3 class="box-name">{{ box.name }}</h3>
                    </div>

                    <div v-if="box.description" class="box-description">
                      <p>{{ box.description }}</p>
                    </div>

                    <div class="box-footer">
                      <div class="selection-indicator">
                        <i class="fas fa-check-circle"></i>
                        <span>Selecionar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Box View -->
        <div v-if="selectedBox" class="box-prescription-container">
          <!-- Header with Back Button -->
          <div class="prescription-header mb-2">
            <div class="header-actions">
              <v-btn class="btn-return" @click="goBack">
                <i class="fas fa-arrow-left mr-2"></i>
                Voltar
              </v-btn>
            </div>

            <div class="header-info">
              <div class="box-indicator">
                <div class="indicator-icon">
                  <i class="fas fa-cube"></i>
                </div>
                <div class="indicator-text">
                  <h2 class="box-title">{{ selectedBox.name }}</h2>
                  <p class="box-subtitle">Integração ativa</p>
                </div>
              </div>
            </div>
          </div>

          <!--
            Abas: 3 tarefas diferentes, antes empilhadas na mesma página.
            Prescrições = trabalho diário; Produção = acompanhamento; Na Box sem origem = conferência.
          -->
          <v-tabs v-model="tab" class="box-tabs" show-arrows>
            <v-tab>
              <i class="fas fa-file-medical mr-2" aria-hidden="true"></i>
              Prescrições
            </v-tab>
            <v-tab>
              <i class="fas fa-industry mr-2" aria-hidden="true"></i>
              Produção
            </v-tab>
            <v-tab>
              <i class="fas fa-search mr-2" aria-hidden="true"></i>
              Na Box sem origem
            </v-tab>
          </v-tabs>

          <div v-if="tab === 1" class="prescription-content box-tab-panel">
            <BoxProductionOrders
              :box_id="selectedBoxId"
              :resident_id="resident_id"
            />
          </div>

          <div v-if="tab === 2" class="box-tab-panel">
            <Prescription
              :resident_id="resident_id"
              :box_id="selectedBoxId"
              @success="retrieve"
            />
          </div>

          <!-- Prescription Content -->
          <div
            v-if="
              tab === 0 && errorsBox.length == 0 && errorsResident.length == 0
            "
            class="prescription-content box-tab-panel"
          >
            <!-- No Prescriptions State -->
            <div
              v-if="group_prescription_medicines.length == 0"
              class="no-prescriptions"
            >
              <div class="no-prescriptions-icon">
                <i class="fas fa-prescription-bottle-alt"></i>
              </div>
              <h3 class="no-prescriptions-title">Sem prescrições ativas</h3>
              <p class="no-prescriptions-text">
                Este residente não possui prescrições disponíveis para
                integração
              </p>
            </div>

            <!-- Prescription Selection and Medicines -->
            <div v-else class="prescription-section">
              <!-- Prescription Selector -->
              <div class="prescription-selector">
                <div class="selector-header">
                  <h3 class="selector-title">
                    <i class="fas fa-file-medical mr-2"></i>
                    Selecionar Prescrição
                  </h3>
                </div>

                <div class="selector-content">
                  <v-select
                    label="Escolha uma prescrição"
                    outlined
                    dense
                    item-value="id"
                    v-model="group_prescription_medicine_id"
                    :items="group_prescription_medicines"
                    @change="selectGroupPrescriptionMedicine"
                    class="modern-select"
                  >
                    <template v-slot:item="{ item }">
                      <div class="prescription-option">
                        <div class="prescription-period">
                          {{ formatDate(item.start_date, false) }} -
                          {{ formatDate(item.end_date, false) }}
                        </div>
                        <div class="prescription-type">
                          <span
                            class="type-badge"
                            :class="
                              item.type === 'LONGO_PRAZO'
                                ? 'type-long-term'
                                : 'type-normal'
                            "
                          >
                            {{ handleTypeGroupPrescription(item.type) }}
                          </span>
                        </div>
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <div class="prescription-selected">
                        {{ formatDate(item.start_date, false) }} -
                        {{ formatDate(item.end_date, false) }} |
                        {{ handleTypeGroupPrescription(item.type) }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </div>

              <!-- Medicines Component -->
              <div
                v-if="group_prescription_medicine_id && medicines.length > 0"
                class="medicines-section"
              >
                <BoxMedicine
                  :box_id="selectedBoxId"
                  :resident_id="resident_id"
                  :group_prescription_medicine_id="
                    group_prescription_medicine_id
                  "
                  :medicines="medicines"
                  :context="medicinesContext"
                  @success="getMedicines"
                />
                <p
                  v-if="loadingMedicines"
                  class="box-loading-medicines"
                  role="status"
                >
                  <i class="fas fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                  Consultando o catálogo da Box…
                </p>
                <ErrorsMessages :errors="medicinesErrors" />
              </div>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div
          v-if="errorsBox.length > 0 || errorsResident.length > 0"
          class="errors-container"
        >
          <div v-if="errorsBox.length > 0" class="error-section">
            <div class="error-header">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              <h4>Erro ao cadastrar empresa na Box</h4>
            </div>
            <ErrorsMessages :errors="errorsBox" />
          </div>

          <div v-if="errorsResident.length > 0" class="error-section">
            <div class="error-header">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              <h4>Erro ao cadastrar residente na Box</h4>
            </div>
            <ErrorsMessages :errors="errorsResident" />
          </div>
        </div>
      </div>

      <!-- Ferramenta de suporte: exige ?box_api_data=1 E permissão de escrita -->
      <v-btn
        v-if="$route.query.box_api_data && permission('write', 'box')"
        @click="deleteBoxApiDataResidentId"
        >Suporte: excluir vínculo do residente com a Box</v-btn
      >
    </b-overlay>
  </div>
</template>

<script>
import ErrorsMessages from "../../Components/ErrorsMessages";
import BoxMedicine from "./Medicine";
import Prescription from "./Prescription";
import BoxProductionOrders from "./ProductionOrders";
import api from "../../../services/api";
import permission from "../../../lib/permission";
import routes from "../../../routes";
import { formatDate } from "../../../lib/format";
import { userData } from "../../../lib/functions";
import toast from "../../../lib/ToastNotification";
import moment from "moment";

export default {
  name: "Box",
  components: {
    ErrorsMessages,
    BoxMedicine,
    Prescription,
    BoxProductionOrders,
  },
  props: {
    resident: Object,
  },
  data() {
    return {
      group_prescription_medicine_id: "",
      resident_id: "",
      box_start_date: "",
      box_cut_off_date: "",
      boxs: [],
      group_prescription_medicines: [],
      group_prescription_medicine: {},
      medicines: [],
      medicinesContext: null,
      medicinesErrors: [],
      loadingMedicines: false,
      tab: 0,
      loading: false,
      selectedBox: null,
      selectedBoxId: null,
      resident_ok: false,
      cpf: null,
      gender_name: null,
      errors: [],
      errorsBox: [],
      errorsResident: [],
    };
  },

  watch: {
    resident() {
      let {
        resident: { box_start_date, box_cut_off_date },
      } = this;

      this.box_start_date = box_start_date
        ? moment(box_start_date).format("YYYY-MM-DD")
        : null;
      this.box_cut_off_date = box_cut_off_date
        ? moment(box_cut_off_date).format("YYYY-MM-DD")
        : null;
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    formatDate,
    permission,

    handleBoxStartDate() {
      let { box_start_date } = this;

      this.box_cut_off_date = moment(box_start_date)
        .subtract(12, "days")
        .format("YYYY-MM-DD");
    },

    handleTypeGroupPrescription(type) {
      if (type == "LONGO_PRAZO") {
        return "LONGO PRAZO";
      }

      return "NORMAL";
    },

    async retrieve() {
      const { id } = routes.history.current.params;

      this.resident_id = id;

      let { user } = userData();

      this.user = user;

      let {
        resident: { box_start_date, box_cut_off_date },
      } = this;

      this.box_start_date = box_start_date
        ? moment(box_start_date).format("YYYY-MM-DD")
        : null;
      this.box_cut_off_date = box_cut_off_date
        ? moment(box_cut_off_date).format("YYYY-MM-DD")
        : null;

      this.group_prescription_medicine_id = "";
      await this.getResident();
      await this.getGroupPrescriptionMedicines();
      await this.getBoxs();
    },

    async getResident() {
      try {
        this.loading = true;

        const { resident_id: id } = this;

        let params = { id };

        const response = await api.get("/rest-home/resident/name", { params });

        const { error, resident } = response.data;

        if (!error) {
          this.cpf = resident.cpf;
          this.gender_name = resident.gender_name;

          if (resident.cpf && resident.gender_name) {
            this.resident_ok = true;
          }
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },

    async getBoxs() {
      try {
        this.loading = true;

        const response = await api.get("/rest-home/box-all");

        const { error, errors, boxs } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.boxs = boxs;
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },

    async getGroupPrescriptionMedicines() {
      try {
        const { resident_id, selectedBoxId } = this;

        let params = {
          resident_id,
          box_id: selectedBoxId,
          type: "NORMAL",
        };

        const response = await api.get(
          "/rest-home/resident/doctor-recipe/group-prescription-medicines",
          { params }
        );

        const { error, errors, group_prescription_medicines } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.group_prescription_medicines = group_prescription_medicines;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async selectBox(box) {
      let { resident_ok, box_start_date } = this;

      if (!resident_ok) {
        this.errors = [
          "Salve as informações nos dados cadastrais do residente para continuar",
        ];
        return;
      }

      if (!box_start_date) {
        this.errors = ["Salve a configuração da data de início"];
        return;
      }

      this.selectedBoxId = box.id;
      this.selectedBox = box;
      await this.registerBox(box.id);
      await this.registerResidentOnBox(box.id, this.resident_id);
    },

    async registerBox(box_id) {
      try {
        let params = { box_id };

        let response = await api.post("/rest-home/box/rest-home", params);

        let { error, message } = response.data;

        if (error) {
          if (message.data && typeof message.data == "object") {
            let errors = Object.entries(message.data);
            for (let error of errors) {
              this.errorsBox.push(error);
            }
          } else {
            this.errorsBox.push(message);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    async registerResidentOnBox(box_id, resident_id) {
      try {
        let params = { box_id, resident_id };

        let response = await api.post("/rest-home/box/resident", params);

        let { error, message } = response.data;

        if (error) {
          if (message.data && typeof message.data == "object") {
            let errors = Object.entries(message.data);
            for (let error of errors) {
              this.errorsResident.push(error);
            }

            if (message.message) {
              this.errorsResident.push(message.message);
            }
          } else {
            this.errorsResident.push(message);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    selectGroupPrescriptionMedicine() {
      this.group_prescription_medicine = this.group_prescription_medicines.find(
        (group_prescription_medicine) =>
          group_prescription_medicine.id == this.group_prescription_medicine_id
      );

      this.medicines = [];

      this.getMedicines();
    },

    async getMedicines() {
      // Recarrega sem esvaziar a lista: o usuário não perde a posição na página após enviar
      this.loadingMedicines = true;
      this.medicinesErrors = [];
      try {
        const { group_prescription_medicine_id, selectedBoxId, resident_id } =
          this;

        const response = await api.get(
          "/rest-home/box/prescription-active-medicines",
          {
            params: {
              group_prescription_medicine_id,
              box_id: selectedBoxId,
              resident_id, // obrigatório no novo fluxo (status e prévia são por residente)
            },
          }
        );

        const { error, errors, medicines, context } = response.data;

        this.medicines = medicines || [];
        this.medicinesContext = context || null;
        if (error) this.medicinesErrors = errors || [];
      } catch (error) {
        this.medicines = [];
        this.medicinesErrors = ["Falha ao consultar os medicamentos da Box."];
      } finally {
        this.loadingMedicines = false;
      }
    },

    goBack() {
      this.group_prescription_medicine_id = null;
      this.medicines = [];
      this.medicinesContext = null;
      this.tab = 0;
      this.selectedBox = null;
      this.selectedBoxId = null;
      this.errorsBox = [];
      this.errorsResident = [];
      this.retrieve();
    },

    async saveConfigBox() {
      try {
        let { resident_id, box_start_date, box_cut_off_date } = this;

        let params = { resident_id, box_start_date, box_cut_off_date };

        let response = await api.put("/rest-home/resident/box/config", params);

        const { error, errors, message } = response.data;

        if (!error) {
          toast(message, "success");

          this.$emit("create");
          this.$emit("configbox");

          this.errors = [];
        } else {
          this.errors = errors;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async deleteBoxApiDataResidentId() {
      try {
        let { resident_id } = this;

        let data = { resident_id };

        await api.delete("/rest-home/resident/box-api-data", { data });
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.box-integration-modern .resident-alert-without-data-to-register-box {
  background-color: #f8f2a0;
  padding: 10px;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 12px;
}

.box-integration-modern {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 100vh;
  position: relative;
}

.debug-info {
  position: relative;
  z-index: 1000;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-content {
  text-align: center;
  color: #64748b;
}

.loading-icon {
  font-size: 48px;
  margin-bottom: 16px;
  color: #0f65b0;
}

.test-section {
  border: 2px dashed #fbbf24 !important;
}

.loading-overlay {
  border-radius: 16px;
}

/* Box Selection */
.box-selection-container {
  max-width: 1200px;
  margin: 0 auto;
}

.no-results {
  text-align: center;
  padding: 80px 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.box-selection-section {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.section-header {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 32px 24px;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
}

.section-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-subtitle {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.boxes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  padding: 32px 24px;
}

.box-card {
  position: relative;
  border-radius: 16px;
  border: 2px solid #e5e7eb;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  min-height: 200px;
}

.box-card:hover {
  border-color: #0f65b0;
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(15, 101, 176, 0.15);
}

.box-card.box-selected {
  border-color: #0f65b0;
  box-shadow: 0 8px 25px rgba(15, 101, 176, 0.2);
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.box-radio {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 20px;
  height: 20px;
  z-index: 10;
}

.box-content {
  position: relative;
  height: 100%;
  min-height: 200px;
}

.box-logo-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.3;
  border-radius: 14px;
}

.box-info {
  position: relative;
  z-index: 2;
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.box-info.with-logo {
  backdrop-filter: blur(1px);
  margin: 8px;
  border-radius: 12px;
}

.box-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.box-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.box-name {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.box-description {
  flex: 1;
  margin-bottom: 16px;
}

.box-description p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.box-footer {
  margin-top: auto;
}

.selection-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0f65b0;
  font-weight: 500;
  font-size: 14px;
}

.box-selected .selection-indicator {
  color: #059669;
}

/* Prescription Container */
.box-prescription-container {
  max-width: 1400px;
  margin: 0 auto;
}

.prescription-header {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 24px;
}

.box-indicator {
  display: flex;
  align-items: center;
  gap: 16px;
}

.indicator-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.box-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.box-subtitle {
  color: #059669;
  font-size: 14px;
  font-weight: 500;
  margin: 4px 0 0 0;
}

/* Prescription Content */
.prescription-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.no-prescriptions {
  text-align: center;
  padding: 80px 24px;
}

.no-prescriptions-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-prescriptions-title {
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.no-prescriptions-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.prescription-section {
  padding: 32px 24px;
}

.prescription-selector {
  margin-bottom: 32px;
}

.selector-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.selector-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.selector-title i {
  color: #0f65b0;
}

.prescription-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  width: 100%;
}

.prescription-period {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

.type-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-normal {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}

.type-long-term {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  color: #ea580c;
}

.prescription-selected {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

/* Error Messages */
.errors-container {
  max-width: 1400px;
  margin: 24px auto 0;
}

.error-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border-left: 4px solid #ef4444;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-header {
  display: flex;
  align-items: center;
  color: #dc2626;
  font-weight: 600;
  margin-bottom: 12px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .box-integration-modern {
    padding: 16px;
  }

  .boxes-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 24px 16px;
  }

  .prescription-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .box-indicator {
    justify-content: center;
    text-align: center;
  }

  .prescription-section {
    padding: 24px 16px;
  }

  .prescription-option {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.box-help {
  display: block;
  margin-top: 4px;
  color: #475569;
  font-size: 13px;
}

.box-tabs {
  margin-bottom: 12px;
  border-radius: 12px;
  overflow: hidden;
}

.box-tab-panel {
  padding: 20px;
}

.box-loading-medicines {
  margin: 12px 0 0;
  color: #475569;
}

/* Vuetify Overrides */
.modern-select >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-select >>> .v-text-field__slot input {
  font-weight: 500 !important;
}
</style>